// Constants
import {REGEX} from '../../constants'

// Hooks
import {useConversionRate} from '@app/custom-contexts'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import {useIntl} from 'react-intl'
import {useToast} from '../../hooks/use-toast'
import {useXSSProtection} from '../../hooks/use-xss-protection'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Utils
import {getConvertedCurrencyCode} from '@app/utils/price-utils'
import {isPastDate} from '../../utils/date'
import {defineMessages} from 'react-intl'

const messages = defineMessages({
    // Generic
    required: {
        id: 'giftcard.form.required',
        defaultMessage: 'This field is required'
    },
    invalid_email: {
        id: 'giftcard.form.error.email',
        defaultMessage: 'Enter a valid email address'
    },
    // Fields
    predefinedAmount_required: {
        id: 'giftcard.form.buy.amount.required',
        defaultMessage: 'Please select an amount'
    },
    customAmount_label: {
        id: 'giftcard.form.buy.amount.label',
        defaultMessage: 'Custom amount'
    },
    customAmount_placeholder: {
        id: 'giftcard.form.buy.amount.placeholder',
        defaultMessage: 'Enter gift card amount'
    },
    customAmount_tooLow: {
        id: 'giftcard.form.error.amount.tooLow',
        defaultMessage: 'Please enter an amount over {minAmmount}'
    },
    customAmount_tooHigh: {
        id: 'giftcard.form.error.amount.tooHigh',
        defaultMessage: 'Please enter an amount under {maxAmmount}'
    },
    customAmount_notInteger: {
        id: 'giftcard.form.error.amount.notInteger',
        defaultMessage: 'No decimals'
    },
    customAmount_helper: {
        id: 'giftcard.form.buy.amount.helper',
        defaultMessage: 'Max amount {maxAmmount} - No Decimals'
    },
    yourName_label: {
        id: 'giftcard.form.buy.yourName.label',
        defaultMessage: 'Your Name'
    },
    friendName_label: {
        id: 'giftcard.form.buy.friendName.label',
        defaultMessage: 'Friend Name'
    },
    friendEmail_label: {
        id: 'giftcard.form.buy.friendEmail.label',
        defaultMessage: 'Friend Email'
    },
    confirmFriendEmail_label: {
        id: 'giftcard.form.buy.confirmFriendEmail.label',
        defaultMessage: 'Confirm Friend Email'
    },
    confirmFriendEmail_emailNotMatch: {
        id: 'giftcard.form.error.email.notMatch',
        defaultMessage: 'The entered email addresses do not match'
    },
    message_label: {
        id: 'giftcard.form.buy.message.label',
        defaultMessage: 'Your message'
    },
    message_helper: {
        id: 'giftcard.form.buy.message.helper',
        defaultMessage: 'You have {remainingCharacters} characters left out of 1000'
    },
    message_tooLong: {
        id: 'giftcard.form.error.message.tooLong',
        defaultMessage: 'This field cannot exceed 1000 characters'
    },
    checkbox_label: {
        id: 'giftcard.form.buy.checkbox.label',
        defaultMessage: 'Send on a special day'
    },
    sendDate_label: {
        id: 'giftcard.form.buy.date.label',
        defaultMessage: 'Choose Date'
    },
    sendDate_invalidDate: {
        id: 'giftcard.form.buy.date.invalid',
        defaultMessage: 'Please enter a valid date'
    }
})

export default function useBuyGiftCardFields({
    form: {
        control,
        watch,
        setValue,
        clearErrors,
        trigger,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage, formatNumber} = useIntl()
    const {currency: activeCurrency} = useCurrency()
    const {validateField} = useXSSProtection()
    const {data: basket} = useCurrentBasket()
    const conversionsRate = useConversionRate()
    const toast = useToast()
    const {app: appConfig} = getConfig()

    const paymentCurrencyCode = getConvertedCurrencyCode(
        conversionsRate,
        basket?.currency || activeCurrency
    )

    const formattedMaxAmmount = formatNumber(
        appConfig?.GC_PREDEFINED_AMOUNTS?.amountsPerCurrency[paymentCurrencyCode]?.max,
        {
            style: 'currency',
            currency: paymentCurrencyCode,
            maximumFractionDigits: 0
        }
    )
    const amounts = appConfig?.GC_PREDEFINED_AMOUNTS?.amountsPerCurrency[
        paymentCurrencyCode
    ]?.amounts?.map((amount) => ({
        value: amount,
        label: formatNumber(amount, {
            style: 'currency',
            currency: paymentCurrencyCode,
            maximumFractionDigits: 0
        })
    }))

    return {
        predefinedAmount: {
            control,
            name: `${prefix}predefinedAmount`,
            type: 'radio',
            options: amounts,
            error: errors?.[`${prefix}predefinedAmount`],
            rules: {
                required: {
                    value: !watch(`${prefix}customAmount`),
                    message: formatMessage(messages.predefinedAmount_required)
                },
                onChange: () => {
                    clearErrors(`${prefix}customAmount`)
                    setValue(`${prefix}customAmount`, '')
                }
            }
        },
        customAmount: {
            control,
            name: `${prefix}customAmount`,
            label: formatMessage(messages.customAmount_label),
            placeholder: formatMessage(messages.customAmount_placeholder),
            defaultValue: '',
            type: 'number',
            autocomplete: 'off',
            error: errors?.[`${prefix}customAmount`],
            rules: {
                min: {
                    value: 1,
                    message: formatMessage(messages.customAmount_tooLow, {
                        minAmmount: formatNumber(1, {
                            style: 'currency',
                            currency: paymentCurrencyCode,
                            maximumFractionDigits: 0
                        })
                    })
                },
                max: {
                    value: appConfig?.GC_PREDEFINED_AMOUNTS?.amountsPerCurrency[paymentCurrencyCode]
                        ?.max,
                    message: formatMessage(messages.customAmount_tooHigh, {
                        maxAmmount: formattedMaxAmmount
                    })
                },
                validate: (value) => {
                    if (!Number.isInteger(Number(value))) {
                        return formatMessage(messages.customAmount_notInteger)
                    } else {
                        return true
                    }
                },
                onChange: () => {
                    clearErrors(`${prefix}predefinedAmount`)
                    setValue(`${prefix}predefinedAmount`, '')
                }
            },
            helpText: formatMessage(messages.customAmount_helper, {
                maxAmmount: formattedMaxAmmount
            })
        },
        yourName: {
            control,
            name: `${prefix}yourName`,
            label: formatMessage(messages.yourName_label),
            placeholder: '',
            defaultValue: '',
            type: 'text',
            autocomplete: 'name',
            error: errors?.[`${prefix}yourName`],
            rules: {
                required: formatMessage(messages.required),
                validate: (value) =>
                    validateField(toast, value, `${prefix}buy-gift-card`, `${prefix}yourName`)
            }
        },
        friendName: {
            control,
            name: `${prefix}friendName`,
            label: formatMessage(messages.friendName_label),
            placeholder: '',
            defaultValue: '',
            type: 'text',
            autocomplete: 'off',
            error: errors?.[`${prefix}friendName`],
            rules: {
                required: formatMessage(messages.required),
                validate: (value) =>
                    validateField(toast, value, `${prefix}buy-gift-card`, `${prefix}friendName`)
            }
        },
        friendEmail: {
            control,
            name: `${prefix}friendEmail`,
            label: formatMessage(messages.friendEmail_label),
            placeholder: '',
            defaultValue: '',
            type: 'email',
            autocomplete: 'off',
            error: errors?.[`${prefix}friendEmail`],
            rules: {
                required: formatMessage(messages.required),
                validate: (value) =>
                    validateField(toast, value, `${prefix}buy-gift-card`, 'friendEmail'),
                pattern: {
                    value: REGEX.EMAIL,
                    message: formatMessage(messages.invalid_email)
                },
                onChange: () => {
                    trigger(`${prefix}confirmFriendEmail`)
                }
            }
        },
        confirmFriendEmail: {
            control,
            name: `${prefix}confirmFriendEmail`,
            label: formatMessage(messages.confirmFriendEmail_label),
            placeholder: '',
            defaultValue: '',
            type: 'email',
            autocomplete: 'off',
            error: errors?.[`${prefix}confirmFriendEmail`],
            rules: {
                required: formatMessage(messages.required),
                pattern: {
                    value: REGEX.EMAIL,
                    message: formatMessage(messages.invalid_email)
                },
                validate: {
                    emailsDontMatch: (value) =>
                        value === watch(`${prefix}friendEmail`) ||
                        formatMessage(messages.confirmFriendEmail_emailNotMatch)
                }
            }
        },
        message: {
            control,
            name: `${prefix}message`,
            type: 'textarea',
            label: formatMessage(messages.message_label),
            defaultValue: '',
            error: errors?.[`${prefix}message`],
            helpText: formatMessage(messages.message_helper, {
                remainingCharacters: 1000 - (watch(`${prefix}message`)?.length ?? 0)
            }),
            rules: {
                maxLength: {
                    value: 1000,
                    message: formatMessage(messages.message_tooLong)
                },
                validate: (value) =>
                    validateField(toast, value, `${prefix}buy-gift-card`, `${prefix}message`)
            }
        },
        checkbox: {
            control,
            name: `${prefix}checkbox`,
            type: 'checkbox',
            label: formatMessage(messages.checkbox_label)
        },
        sendDate: {
            control,
            name: `${prefix}sendDate`,
            label: formatMessage(messages.sendDate_label),
            defaultValue: '',
            type: 'date',
            error: errors[`${prefix}sendDate`],
            rules: {
                required: formatMessage(messages.required),
                validate: (value) => {
                    if (isPastDate(value)) {
                        return formatMessage(messages.sendDate_invalidDate)
                    }
                    return true
                }
            }
        }
    }
}
